import { computed, ref } from 'vue'
import api from '@/services/api'
import apiClient from '@/services/axios'
import {
  promotionLinksSocketService,
  type PromotionDomainCheckedPayload,
  type PromotionDomainStatus,
} from '@/services/promotionLinksSocketService'
import { dispatchPromotionEnableChanged } from '@/utils/promotionEnableSync'

export interface AdminPromotionDeadNotification {
  id: string
  promotionId: number
  domainUrl: string
  timestamp: string
  read: boolean
  enable: 0 | 1
  type: PromotionDomainStatus
}

interface AlertRecord {
  id: number
  promotion_link_id: number
  type: PromotionDomainStatus | string
  domain_url: string
  description?: string
  payload?: string | PromotionDomainCheckedPayload
  created_at: string
}

const PAGE_SIZE = 10

const notifications = ref<AdminPromotionDeadNotification[]>([])
const togglingIds = ref(new Set<string>())
const currentPage = ref(0)
const hasMore = ref(true)
const isLoading = ref(false)
const isLoadingMore = ref(false)
let listenerRegistered = false
let activeConsumers = 0

function parsePayload(raw: AlertRecord['payload']): Partial<PromotionDomainCheckedPayload> {
  if (!raw) return {}
  if (typeof raw === 'object') return raw
  try {
    return JSON.parse(raw) as PromotionDomainCheckedPayload
  } catch {
    return {}
  }
}

function mapAlertToNotification(alert: AlertRecord): AdminPromotionDeadNotification | null {
  if (!alert?.id) return null

  const payload = parsePayload(alert.payload)
  const type: PromotionDomainStatus =
    alert.type === 'DOMAIN_ACTIVE' || payload.type === 'DOMAIN_ACTIVE'
      ? 'DOMAIN_ACTIVE'
      : 'DOMAIN_DEAD'

  return {
    id: String(alert.id),
    promotionId: Number(alert.promotion_link_id ?? payload.promotionId),
    domainUrl: alert.domain_url || payload.domainUrl || '',
    timestamp: payload.timestamp || new Date(alert.created_at).toISOString(),
    read: true,
    enable: 1,
    type,
  }
}

function isSameNotification(
  left: AdminPromotionDeadNotification,
  right: AdminPromotionDeadNotification,
) {
  return (
    left.id === right.id ||
    (left.promotionId === right.promotionId &&
      left.type === right.type &&
      left.domainUrl === right.domainUrl &&
      left.timestamp === right.timestamp)
  )
}

function buildLiveNotification(payload: PromotionDomainCheckedPayload): AdminPromotionDeadNotification {
  const timestamp = payload.timestamp || new Date().toISOString()
  return {
    id: `live-${payload.promotionId}-${payload.type}-${timestamp}`,
    promotionId: payload.promotionId,
    domainUrl: payload.domainUrl,
    timestamp,
    read: false,
    enable: 1,
    type: payload.type,
  }
}

function addLiveNotification(payload: PromotionDomainCheckedPayload) {
  const next = buildLiveNotification(payload)
  if (notifications.value.some((item) => isSameNotification(item, next))) {
    return false
  }

  notifications.value = [next, ...notifications.value]
  return true
}

const handleDomainChecked = (payload: PromotionDomainCheckedPayload) => {
  addLiveNotification(payload)
}

async function fetchAlertsPage(page: number) {
  const res = await apiClient.post(api.alertList(), {
    page,
    limit: PAGE_SIZE,
  })

  const records = Array.isArray(res.data?.data) ? (res.data.data as AlertRecord[]) : []
  const mapped = records
    .map(mapAlertToNotification)
    .filter((item): item is AdminPromotionDeadNotification => item !== null)

  const pagination = res.data?.pagination as
    | { page?: number; limit?: number; total?: number; totalPages?: number }
    | undefined

  const totalPages = Number(pagination?.totalPages) || (mapped.length >= PAGE_SIZE ? page + 1 : page)

  return {
    mapped,
    hasMore: page < totalPages && mapped.length >= PAGE_SIZE,
  }
}

async function loadInitialAlerts() {
  if (isLoading.value) return

  currentPage.value = 1
  hasMore.value = true
  isLoading.value = true

  try {
    const result = await fetchAlertsPage(1)
    const liveItems = notifications.value.filter((item) => item.id.startsWith('live-'))
    notifications.value = [
      ...liveItems.filter(
        (live) => !result.mapped.some((item) => isSameNotification(item, live)),
      ),
      ...result.mapped,
    ]
    hasMore.value = result.hasMore
  } catch (error) {
    console.error('Failed to load promotion alerts:', error)
    hasMore.value = false
  } finally {
    isLoading.value = false
  }
}

async function loadMoreAlerts() {
  if (isLoading.value || isLoadingMore.value || !hasMore.value) return

  const nextPage = currentPage.value + 1
  isLoadingMore.value = true

  try {
    const result = await fetchAlertsPage(nextPage)
    const existing = notifications.value
    const appended = result.mapped.filter(
      (item) => !existing.some((current) => isSameNotification(current, item)),
    )

    notifications.value = [...existing, ...appended]
    currentPage.value = nextPage
    hasMore.value = result.hasMore
  } catch (error) {
    console.error('Failed to load more promotion alerts:', error)
    hasMore.value = false
  } finally {
    isLoadingMore.value = false
  }
}

export function useAdminPromotionDeadNotifications() {
  const unreadCount = computed(() => notifications.value.filter((item) => !item.read).length)

  const initializePromotionDeadNotifications = async () => {
    activeConsumers += 1

    if (!listenerRegistered) {
      promotionLinksSocketService.on('domain:checked', handleDomainChecked)
      listenerRegistered = true
    }

    try {
      await Promise.all([
        promotionLinksSocketService.connect(),
        loadInitialAlerts(),
      ])
    } catch (error) {
      console.error('Failed to connect promotion links socket:', error)
    }
  }

  const cleanupPromotionDeadNotifications = () => {
    activeConsumers = Math.max(0, activeConsumers - 1)
    if (activeConsumers > 0) return

    promotionLinksSocketService.off('domain:checked', handleDomainChecked)
    promotionLinksSocketService.disconnect()
    listenerRegistered = false
  }

  const markAsRead = (id: string) => {
    const target = notifications.value.find((item) => item.id === id)
    if (!target || target.read) return
    target.read = true
  }

  const markAllAsRead = () => {
    if (notifications.value.every((item) => item.read)) return
    notifications.value = notifications.value.map((item) => ({ ...item, read: true }))
  }

  const clearAll = () => {
    notifications.value = []
  }

  const toggleNotificationEnable = async (notification: AdminPromotionDeadNotification): Promise<0 | 1 | null> => {
    if (togglingIds.value.has(notification.id)) return null

    togglingIds.value.add(notification.id)
    try {
      const fallbackNextEnable = notification.enable === 1 ? 0 : 1
      const res = await apiClient.post(api.enablePromotion(notification.promotionId))
      const nextEnable = Number(res.data?.data?.enable ?? fallbackNextEnable) === 1 ? 1 : 0

      notification.enable = nextEnable
      dispatchPromotionEnableChanged({
        promotionId: notification.promotionId,
        enable: nextEnable,
      })

      return nextEnable
    } catch (error) {
      console.error('Failed to toggle promotion enable from notification:', error)
      throw error
    } finally {
      togglingIds.value.delete(notification.id)
    }
  }

  return {
    notifications,
    unreadCount,
    togglingIds,
    hasMore,
    isLoading,
    isLoadingMore,
    initializePromotionDeadNotifications,
    cleanupPromotionDeadNotifications,
    loadMoreAlerts,
    markAsRead,
    markAllAsRead,
    clearAll,
    toggleNotificationEnable,
  }
}
